/**
 * Settings & initialization actions — group, period, viewing learner, dev/demo.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import type { GroupId, StartingPeriod } from '../../config/systemConfig'
import { GROUPS } from '../../config/systemConfig'
import { DEFAULT_GROUP } from '../../config/constants'
import {
  ensureAdminAccount,
  loadState,
  saveState,
  createFreshState,
} from '../../accounts'
import { dataLayer } from '../../data/dataLayer'
import { debug, error as logError, warn } from '../../utils/logger'
import { loadDemoState, seedDemoAssessmentsForState } from '../../demo'
import { evictStaleAttempts } from '../../mastery'
import type { MultiAccountStateV4 } from '../../accounts'

/**
 * Boot-time LRU eviction for question-attempt history.
 *
 * The §6.2 LRU storage quota feature (`evictStaleAttempts`) was implemented
 * but had zero callers — `questionStates` grew unbounded over time. This
 * sweeps each learner's history once per boot, dropping entries older than
 * `ATTEMPT_CONFIG.MAX_AGE_MS` (90 days). Family-mastery (canonical) data is
 * NOT touched. Returns the cleaned multiAccountState plus a summary count.
 */
function evictStaleAttemptsAcrossLearners(state: MultiAccountStateV4): {
  state: MultiAccountStateV4
  totalEvicted: number
} {
  const cleanedLearnerData: Record<
    string,
    MultiAccountStateV4['learnerData'][string]
  > = {}
  let totalEvicted = 0
  for (const [learnerId, learnerState] of Object.entries(state.learnerData)) {
    if (!learnerState.questionStates) {
      cleanedLearnerData[learnerId] = learnerState
      continue
    }
    const { cleaned, report } = evictStaleAttempts(learnerState.questionStates)
    if (report.evicted > 0) {
      cleanedLearnerData[learnerId] = {
        ...learnerState,
        questionStates: cleaned,
      }
      totalEvicted += report.evicted
    } else {
      cleanedLearnerData[learnerId] = learnerState
    }
  }
  if (totalEvicted === 0) return { state, totalEvicted: 0 }
  return {
    state: { ...state, learnerData: cleanedLearnerData },
    totalEvicted,
  }
}

type SettingsActions = Pick<
  AppStoreState,
  | 'initialize'
  | 'setSelectedGroup'
  | 'setStartingPeriod'
  | 'setViewingLearner'
  | 'devResetProgress'
  | 'loadDemoAccounts'
>

export function createSettingsActions(set: SetFn, get: GetFn): SettingsActions {
  return {
    initialize: async () => {
      const state = get()
      if (state.isInitialized) return

      // Load persisted group/period from dataLayer
      try {
        const storedGroup = await dataLayer.getGroup()
        if (storedGroup) {
          const parsed = storedGroup as GroupId
          if (GROUPS.includes(parsed)) {
            set({ selectedGroup: parsed })
          }
        }
        const storedPeriod = await dataLayer.getPeriod()
        if (storedPeriod) {
          set({ manualPeriod: storedPeriod as StartingPeriod })
        }
      } catch {
        // Ignore parse errors
      }

      // Load multi-account state (V4 only).
      //
      // Boot-deadlock fix (audit P-1, app-store): wrap the entire load
      // chain in try/catch so a corrupt localStorage / quota error /
      // schema-migration crash can never leave `isInitialized` permanently
      // false — which would lock the UI on its first skeleton forever.
      //
      // Boot-deadlock fix v2 (mei 2026): wrap the whole thing in a 4-second
      // race-against-timeout. If the storage layer itself hangs (Safari
      // private mode, a stuck IndexedDB shim, a content-script that locks
      // localStorage), we forcibly fall back to a fresh state so the UI
      // unblocks instead of staring at the skeleton forever.
      const BOOT_TIMEOUT_MS = 4000
      const loadWithTimeout = async () => {
        const loadPromise = (async () => {
          const loadedState = await loadState()
          const accountsEnsured = {
            ...loadedState,
            accounts: ensureAdminAccount(loadedState.accounts),
          }
          // LRU eviction pass — §6.2 (drop attempt-history entries older
          // than 90 days). One-shot per boot; familyMastery untouched.
          const { state: migratedState, totalEvicted } =
            evictStaleAttemptsAcrossLearners(accountsEnsured)
          if (totalEvicted > 0) {
            debug(
              `[initialize] LRU eviction: dropped ${totalEvicted} stale question-attempt entries`,
            )
          }
          saveState(migratedState)
          return migratedState
        })()
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(
            () =>
              reject(new Error('Boot timeout — storage layer unresponsive')),
            BOOT_TIMEOUT_MS,
          )
        })
        return Promise.race([loadPromise, timeoutPromise])
      }
      try {
        const migratedState = await loadWithTimeout()
        set({ multiAccountState: migratedState, isInitialized: true })
      } catch (err) {
        logError(
          '[initialize] Failed to load persisted state — falling back to fresh state',
          err,
        )
        const freshState = createFreshState()
        const migratedFresh = {
          ...freshState,
          accounts: ensureAdminAccount(freshState.accounts),
        }
        try {
          saveState(migratedFresh)
        } catch {
          /* swallow — storage may be disabled (private mode) */
        }
        set({ multiAccountState: migratedFresh, isInitialized: true })
      }
    },

    /**
     * Explicit override of the active group.
     *
     * ⚠️ For most reads, prefer `deriveSelectedGroup(state)` or the
     * `selectedGroup` value exposed by `useAppStore` — both auto-derive
     * from learner.grade / viewing learner context.
     *
     * Use this action only for legitimate overrides such as:
     * - Admin "preview as group N" tooling
     * - Loading demo state (where the active demo learner's grade is applied)
     *
     * Routine sync from learner.grade is handled automatically; calling this
     * directly to "switch the user to another group" is almost always a bug.
     */
    setSelectedGroup: (group) => {
      if (!GROUPS.includes(group)) {
        warn(`Invalid group "${group}" — ignoring setSelectedGroup call`)
        return
      }
      set((s) => ({
        selectedGroup: group,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(group)
      debug(`Group switched to ${group}`)
    },

    setStartingPeriod: (period) => {
      set((s) => ({
        manualPeriod: period,
        groupVersion: s.groupVersion + 1,
      }))
      if (period) {
        dataLayer.setPeriod(period)
      } else {
        dataLayer.clearPeriod()
      }
      debug(`Starting period set to ${period || 'AUTO'} - context updated`)
    },

    setViewingLearner: (learnerId) => {
      // Verdedigingslaag: accepteer alleen een leerling die daadwerkelijk aan
      // het actieve account gekoppeld is. De UI biedt nooit iets anders aan,
      // maar deze actie was vanaf de console met elk willekeurig id aan te
      // roepen. Zolang alle data lokaal staat is de schade beperkt tot wat er
      // al op dit apparaat stond — maar zodra een portaal deze keuze gebruikt
      // om serverdata op te halen, is een ongecontroleerd id een echt lek.
      // Nu weigeren is goedkoper dan het later moeten onthouden.
      if (learnerId) {
        const s = get()
        const active = s.multiAccountState?.activeAccountId
        const account = active
          ? s.multiAccountState?.accountsById?.[active]
          : undefined
        const rel = s.multiAccountState?.relationships
        const allowed =
          account?.role === 'admin' ||
          learnerId === active ||
          (rel?.parentToLearners?.[active ?? ''] ?? []).includes(learnerId) ||
          (rel?.teacherToLearners?.[active ?? ''] ?? []).includes(learnerId)
        if (!allowed) {
          warn(
            `setViewingLearner geweigerd: ${learnerId} is niet gekoppeld aan het actieve account`,
          )
          return
        }
      }
      set({ viewingLearnerId: learnerId })
      debug(`Viewing learner set to: ${learnerId || 'none'}`)
    },

    devResetProgress: () => {
      const freshState = createFreshState()
      set((s) => ({
        multiAccountState: freshState,
        groupVersion: s.groupVersion + 1,
      }))
      saveState(freshState)
    },

    loadDemoAccounts: async () => {
      // Demo path: ensure content is loaded, then generate profiles + assessments.
      // This is the ONE path that requires all 10 modules upfront.
      const demoState = await loadDemoState()
      const multiAccountState = demoState.multiAccountState

      // Djayden (grade 7) is the active account — sync selectedGroup
      const activeId = multiAccountState.accounts.activeAccountId
      const activeAccount = multiAccountState.accounts.accountsById[activeId]
      const activeGrade =
        activeAccount?.role === 'learner' && 'grade' in activeAccount
          ? (activeAccount as { grade?: number }).grade
          : undefined
      const groupToSet =
        activeGrade && GROUPS.includes(activeGrade as GroupId)
          ? (activeGrade as GroupId)
          : DEFAULT_GROUP
      set((s) => ({
        multiAccountState,
        selectedGroup: groupToSet,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(groupToSet)
      saveState(multiAccountState)

      // Pick a teacher + classroom for assessment seeding
      const meesterJay = Object.values(
        multiAccountState.accounts.accountsById,
      ).find((a) => a.displayName === 'Meester Jay')
      const firstClassroomId = Object.keys(
        multiAccountState.accounts.classrooms || {},
      )[0]

      try {
        await seedDemoAssessmentsForState(demoState, {
          teacherId: meesterJay?.id,
          classroomId: firstClassroomId,
        })
      } catch (err) {
        logError('Failed to seed demo assessments', err)
      }

      debug('✅ Demo accounts loaded successfully')
    },
  }
}
